import React, {useCallback, useEffect, useState} from 'react'
import {string, object, bool, func} from 'prop-types'
import {
    Loading,
    Text, Button, Selector, A, Icon
} from '@rebrandly/styleguide'
import './QrCodeForm.css'
import {composeQrCodeUrl} from "./utils/utils";
import {useDebounce} from "use-lodash-debounce";
import {get} from "lodash";
import ShareModal from "../Share/ShareModal";
import useModalUrlParam from "../hooks/useModalUrlParam";

const BACKGROUND_KEY = 'options.colorLight'
const MODULES_KEY = 'options.colorDark'
const MARGIN_KEY = 'options.margin'
const RATIO_KEY = 'options.logoRatio'


const SIZE = [
    { label: '64 px', value: 64 },
    { label: '128 px', value: 128 },
    { label: '256 px', value: 256 },
    { label: '512 px', value: 512 }
]
const TYPE = [
    { label: 'PNG', value: 'PNG' },
    { label: 'SVG', value: 'SVG' }
]

const QrCodeForm = ({shortUrl, settings, canDownload, showLink, copyToClipboard}) => {
    const [loading, setLoading] = useState(true)
    const [selectedSize, setSelectedSize] = useState(SIZE[0])
    const [selectedType, setSelectedType] = useState(TYPE[0])
    const [mounted, setMounted] = useState(false)
    const [showShareModal, setShowShareModal] = useState(false)

    const { setModalParam, removeModalParam } = useModalUrlParam();

    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        setLoading(true)
    }, [shortUrl])

    useEffect(() => {
        setLoading(true)
    }, [settings])

    const handleShowShareModal = useCallback(() => {
        setModalParam('share')
        setShowShareModal(true);
    }, [setShowShareModal]);

    const handleCloseShareModal = useCallback(() => {
        removeModalParam();
        setShowShareModal(false);
    }, [setShowShareModal]);


    const debouncedBackgroundColor = useDebounce(get(settings, BACKGROUND_KEY), 750, {maxWait: 1000})
    const debouncedModuleColor = useDebounce(get(settings, MODULES_KEY), 750, {maxWait: 1000})
    const debouncedRatio = useDebounce(get(settings, RATIO_KEY), 750, {maxWait: 1000})
    const debouncedMargin = useDebounce(get(settings, MARGIN_KEY), 500, {maxWait: 1000})

    const qrCodeURL = useCallback((size) => {
        let dummyParam = settings?.logo + Math.random() // @dev this is a hack to prevent img tag from caching the image and not updating the logo after is uploaded
        let debouncedSettings = { options: {
                size: size || 200,
                margin: debouncedMargin,
                ratio: debouncedRatio,
                colorLight: debouncedBackgroundColor,
                colorDark: debouncedModuleColor
            }
        }

        return `${composeQrCodeUrl(shortUrl, debouncedSettings)}&dummy=${dummyParam}`
    }, [debouncedModuleColor, debouncedBackgroundColor, debouncedRatio, debouncedMargin, settings?.logo, shortUrl])

    const handleDownload = (size) => {
        let type = size ? '' : '&type=svg'
        window.open(`${qrCodeURL(selectedSize?.value)}${type}`, '_blank')
    }

    const handleChangeSelectedType = (type) => {
        // svg
        if (type === TYPE[1]) {
            setSelectedSize(undefined);
        } else {
            setSelectedSize(SIZE[0]);
        }
        setSelectedType(type)
    }

    return (
        <div className='QRCodeForm__container'>
            <div className='QRCodeForm__img m-b-24'>
                {mounted && <img src={qrCodeURL(null)} onLoad={() => setLoading(false)} alt='qr-code' />}
                {loading && <div className='QRCodeForm__preview-loading'>
                    <Loading />
                </div>}
            </div>
            {mounted && showLink && (
                <>
                    <div className='QRCodeForm__shortLink p-b-8'>
                        <A className='text-center p-r-4' size='x-lg' target="_blank" href={shortUrl}><span className='font-bold'>{shortUrl.split('//')[1]}</span></A>
                        {typeof copyToClipboard === 'function' && (
                            <Button theme="ghost" size="sm" onClick={copyToClipboard}>
                                <Icon name="ic-copy" color="var(--color-black)" />
                            </Button>
                        )}
                        <Button theme="ghost" size='sm' onClick={handleShowShareModal}><Icon name='ic-share' color='var(--color-black)'/> </Button>
                    </div>
                </>
            )}
            <div>
                <Text size='small' className='font-bold p-b-4'>Export format</Text>
                <div className='QrCodeForm__Selector p-b-24'>
                    <Selector
                        options={SIZE}
                        value={selectedSize}
                        onChange={(size) => {setSelectedSize(size)}}
                        placeholder='Select'
                        disabled={selectedType === TYPE[1]}
                        closeOnSelect
                    />
                    <Selector
                        options={TYPE}
                        value={selectedType}
                        onChange={(type) => handleChangeSelectedType(type)}
                        closeOnSelect
                    />
                </div>
                {canDownload && (
                    <Button
                        label='Download'
                        className='Button--block'
                        theme='primary'
                        onClick={() => handleDownload(selectedSize?.value)}
                    />
                )}
            </div>
            <ShareModal
                isOpen={showShareModal}
                onClose={handleCloseShareModal}
                shortUrl={shortUrl}
            />
        </div>
    )
}

QrCodeForm.propTypes = {
    shortUrl: string.isRequired,
    settings: object,
    canDownload: bool,
    showLink: bool,
    copyToClipboard: func
}

QrCodeForm.defaultProps = {
    settings: null,
    canDownload: true,
    showLink: false
}

export default QrCodeForm